/* NAV compliance-panel — hiányzó bevallás · köztartozás · KOMA (9. lépés).
 *
 * ⚠⚠ EZ NEM AZ ADÓFOLYÓSZÁMLA. Három másik NAV-végpont; ugyanarra a kérdésre
 * válaszolnak („mit tud rólunk a NAV"), ezért ül egy lapon az adófolyószámlával,
 * de az adatok külön táblákból jönnek.
 *
 * HÁROM DOLOG, AMI ITT SZÁNDÉKOS ÉS NE „EGYSZERŰSÍTSD":
 *
 * 1. A KOMA HÁROMÁLLAPOTÚ: igen / nem / **nem tudjuk** (`null`). A `null`-t
 *    TILOS „nem"-ként megjeleníteni: az azt állítaná, hogy a cég NEM
 *    köztartozásmentes — ami közbeszerzésen kizáró ok. Ezért van a „nincs adat"
 *    külön, semleges megjelenítése.
 *
 * 2. A hiányzó bevallásnál a „rendezve" **JÓ HÍR** (beadták), nem
 *    figyelmeztetés. Ezért zöld és alapból elrejtett — a nyitott tételek a
 *    cselekvésre hívó információ.
 *
 * 3. A KOMA-minősítés és a köztartozás-hátralék ELLENTMONDHAT egymásnak (a
 *    sandboxban mérve: minősítés „igen", miközben 6 000 Ft hátralék van). Nem
 *    „javítjuk" össze őket: két külön NAV-válasz, mindkettőt úgy mutatjuk, ahogy
 *    jött. Ha ellentmondanak, azt a felhasználónak LÁTNIA kell.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, ShieldCheck, ShieldAlert, ShieldQuestion, FileWarning,
         CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { navUpoApi } from '../services/api';
import { Btn, Card, Alert, Amount, Badge, Spinner, Checkbox,
         Truncate } from './UI';
import { useToast } from './Toast';

const fmtDate = (v) => v ? v.replace(/-/g, '.') + '.' : '—';
const fmtTs = (iso) => iso
  ? new Date(iso).toLocaleString('hu-HU', { timeZone: 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit' })
  : '—';

const th = {
  textAlign: 'left', padding: '0.4rem 0.6rem', fontSize: 'var(--fs-xs)',
  fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.05em', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};
const td = {
  padding: '0.42rem 0.6rem', fontSize: '0.8rem',
  borderBottom: '1px solid var(--border-subtle, var(--border))',
  verticalAlign: 'top',
};

/* Az időszak emberi alakja a NAV tömör kódjából. A backend már dekódolta; ha
 * nem sikerült (`idoszak_tipus` null), a NYERS kódot mutatjuk — nem tippelünk. */
function idoszakLabel(t) {
  if (t.idoszak_tipus === 'EV')       return `${t.idoszak_ev}. év`;
  if (t.idoszak_tipus === 'NEGYEDEV') return `${t.idoszak_ev}. ${'I'.repeat(Math.min(t.idoszak_sorszam, 3)) || ''}${t.idoszak_sorszam === 4 ? 'IV' : ''}. negyedév`;
  if (t.idoszak_tipus === 'HONAP')    return `${t.idoszak_ev}.${String(t.idoszak_sorszam).padStart(2, '0')}.`;
  return t.bevallas_idoszak || '—';
}

/* ⚠ A háromállapotú KOMA megjelenítése. A `null` SEMLEGES, nem negatív. */
function KomaJelzo({ label, value, hint }) {
  const s = value === true
    ? { Icon: ShieldCheck,    color: 'var(--success)', text: 'IGEN' }
    : value === false
    ? { Icon: ShieldAlert,    color: 'var(--danger)',  text: 'NEM' }
    : { Icon: ShieldQuestion, color: 'var(--text-muted)', text: 'nincs adat' };
  return (
    <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
      <s.Icon size={18} style={{color:s.color,flexShrink:0,marginTop:2}}/>
      <div>
        <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>{label}</div>
        <div style={{fontSize:'0.95rem',fontWeight:600,color:s.color}}>{s.text}</div>
        {/* ⚠ `--text-muted`, NEM `--text-dim`: ez a sor TARTALOM („közbeszerzésnél
            ezt kérik") — az mondja meg, MIÉRT érdekes a minősítés. Gábor
            visszajelzése (2026-08-05): „olvashatatlan. pici és sötét." */}
        {hint && <div style={{fontSize:'var(--fs-xs)',
          color:'var(--text-muted)'}}>{hint}</div>}
      </div>
    </div>
  );
}

export default function NavCompliance() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showResolved, setShowResolved] = useState(false);
  /* ⚠ A hiányzó bevallások listája ALAPBÓL ÖSSZECSUKVA (Gábor, 2026-08-05:
     „a hiányzó bevallások is legyen lenyitható fül"). Élesben ez 25–26 sor, ami
     elnyomja a fölötte lévő három összegző kártyát. A DARABSZÁM viszont a kártyán
     és a nyitó-fejlécen is látszik, tehát a cselekvésre hívó információ nem
     tűnik el — csak a részletezés van egy kattintásra. */
  const [listOpen, setListOpen] = useState(false);

  const { data = {}, isLoading } = useQuery({
    queryKey: ['nav-upo-compliance'], queryFn: navUpoApi.compliance,
  });
  // A NAV ÜPO cégenkénti kapcsolója — kikapcsolva a lekérdezés gomb tiltott (2026-09-27).
  const { data: upoState = {} } = useQuery({
    queryKey: ['nav-upo-state'], queryFn: navUpoApi.state,
  });
  const upoEnabled = upoState.enabled === true;

  const syncMut = useMutation({
    mutationFn: () => navUpoApi.complianceSync(),
    onSuccess: (d) => {
      const hibas = (d.results || []).filter(r => !r.ok);
      if (hibas.length) {
        // ⚠ Az egyik végpont hibája nem veszi el a többit (a KOMA NAV-oldali
        // készültsége kérdéses volt) — ezért részleges siker is van.
        toast.info(`Kész, de ${hibas.length} végpont hibázott: `
                 + hibas.map(r => r.endpoint).join(', '));
      } else {
        toast.success('A NAV compliance-adatok frissítve.');
      }
      qc.invalidateQueries({ queryKey: ['nav-upo-compliance'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A frissítés nem sikerült.'),
  });

  if (isLoading) return <Spinner/>;

  const { koma, koztartozas: kozt, hianyzo_bevallas: hb = {} } = data;
  const tetelek = (hb.tetelek || []).filter(
    t => showResolved || !t.resolved_at);
  const semmiSincs = !koma && !kozt && !hb.queried_at;

  // ⚠ A két NAV-válasz ellentmondhat — ezt kiírjuk, nem simítjuk el.
  const ellentmondas = koma?.minosites === true
    && (kozt?.osszes_hatralek || 0) > 0;

  return (
    <Card title="Bevallások és köztartozás"
      subtitle="A NAV három további lekérdezése: hiányzó bevallások, köztartozás jogcím szerint, és a KOMA (köztartozásmentes adózói adatbázis) státusz."
      actions={
        <Btn size="sm" variant="ghost" loading={syncMut.isPending} disabled={!upoEnabled}
          title={upoEnabled ? undefined : 'A NAV ÜPO ki van kapcsolva (Beállítások → NAV adófolyószámla).'}
          onClick={() => syncMut.mutate()}>
          <RefreshCw size={14}/>Lekérdezés a NAV-tól
        </Btn>}>

      {semmiSincs ? (
        <Alert type="info">
          Még nem futott compliance-lekérdezés. Kattints a „Lekérdezés a NAV-tól"
          gombra.
        </Alert>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* ── KOMA + köztartozás ── */}
          <div style={{display:'grid',gap:12,
            gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))'}}>
            <div style={{border:'1px solid var(--border)',
              borderRadius:'var(--radius)',padding:'0.8rem'}}>
              <div style={{fontSize:'0.7rem',fontWeight:600,
                color:'var(--text-muted)',textTransform:'uppercase',
                letterSpacing:'0.06em',marginBottom:10}}>KOMA</div>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                <KomaJelzo label="Szerepel a KOMA-adatbázisban"
                  value={koma?.szereples}/>
                <KomaJelzo label="Köztartozásmentes minősítés"
                  value={koma?.minosites}
                  hint="közbeszerzésnél ezt kérik"/>
              </div>
              <div style={{fontSize:'var(--fs-xs)',color:'var(--text-dim)',
                marginTop:8}}>lekérdezve: {fmtTs(koma?.queried_at)}</div>
            </div>

            <div style={{border:'1px solid var(--border)',
              borderRadius:'var(--radius)',padding:'0.8rem'}}>
              <div style={{fontSize:'0.7rem',fontWeight:600,
                color:'var(--text-muted)',textTransform:'uppercase',
                letterSpacing:'0.06em',marginBottom:10}}>Köztartozás</div>
              <div style={{fontSize:'1.3rem',fontWeight:700,
                fontFamily:'var(--mono)',
                color:(kozt?.osszes_hatralek || 0) > 0
                  ? 'var(--danger)' : 'var(--success)'}}>
                <Amount value={kozt?.osszes_hatralek ?? 0}/>
              </div>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>
                fennálló hátralék
              </div>
              <div style={{fontSize:'var(--fs-xs)',color:'var(--text-dim)',
                marginTop:8}}>lekérdezve: {fmtTs(kozt?.queried_at)}</div>
            </div>

            <div style={{border:'1px solid var(--border)',
              borderRadius:'var(--radius)',padding:'0.8rem'}}>
              <div style={{fontSize:'0.7rem',fontWeight:600,
                color:'var(--text-muted)',textTransform:'uppercase',
                letterSpacing:'0.06em',marginBottom:10}}>Hiányzó bevallás</div>
              <div style={{fontSize:'1.3rem',fontWeight:700,
                fontFamily:'var(--mono)',
                color:(hb.nyitott_db || 0) > 0 ? 'var(--warning)' : 'var(--success)'}}>
                {hb.nyitott_db ?? 0}
              </div>
              <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>
                még nincs beadva
                {/* ⚠ „rendezve" = beadták: JÓ hír, ezért zölden és külön. */}
                {hb.rendezett_db > 0 && (
                  <span style={{color:'var(--success)'}}>
                    {' '}· {hb.rendezett_db} rendezve
                  </span>)}
              </div>
              <div style={{fontSize:'var(--fs-xs)',color:'var(--text-dim)',
                marginTop:8}}>lekérdezve: {fmtTs(hb.queried_at)}</div>
            </div>
          </div>

          {ellentmondas && (
            <Alert type="warning">
              A NAV <b>köztartozásmentesnek</b> minősít, ugyanakkor{' '}
              <b><Amount value={kozt.osszes_hatralek}/></b> fennálló hátralékot is
              jelez. A kettő két külön NAV-válasz — szándékosan mindkettőt úgy
              mutatjuk, ahogy jött. Ha éles adaton látod, érdemes a NAV-nál
              rákérdezni.
            </Alert>
          )}

          {/* ── Köztartozás jogcím szerint ─────────────────────────────────
              ⚠ JOGCÍM, nem adónem: más kódtér, mint az adófolyószámláé. */}
          {kozt?.jogcimek?.length > 0 && (
            <div>
              <div style={{fontSize:'0.78rem',fontWeight:600,marginBottom:6}}>
                Köztartozás jogcím szerint
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:640,
                  tableLayout:'fixed'}}>
                  <colgroup>
                    <col style={{width:92}}/>
                    <col/>
                    <col style={{width:130}}/>
                    <col style={{width:130}}/>
                    <col style={{width:130}}/>
                  </colgroup>
                  <thead><tr>
                    <th style={th}>Jogcím</th>
                    <th style={th}>Elnevezés</th>
                    <th style={{...th,textAlign:'right'}}>Előírás</th>
                    <th style={{...th,textAlign:'right'}}>Befizetés</th>
                    <th style={{...th,textAlign:'right'}}>Hátralék</th>
                  </tr></thead>
                  <tbody>
                    {kozt.jogcimek.map(j => (
                      <tr key={j.jogcim_kod}>
                        <td style={{...td,fontFamily:'var(--mono)'}}>{j.jogcim_kod}</td>
                        <td style={td}>
                          <Truncate>{j.elnevezes || '—'}</Truncate></td>
                        <td style={{...td,textAlign:'right'}}>
                          <Amount value={j.eloiras ?? 0}/></td>
                        <td style={{...td,textAlign:'right'}}>
                          <Amount value={j.befizetes ?? 0}/></td>
                        <td style={{...td,textAlign:'right',fontWeight:600}}>
                          <Amount value={j.fennallo_hatralek ?? 0}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Hiányzó bevallások ── */}
          {(hb.tetelek || []).length > 0 && (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',
                alignItems:'center',marginBottom:6,gap:12,flexWrap:'wrap'}}>
                <button type="button" onClick={() => setListOpen(v => !v)}
                  style={{background:'none',border:'none',padding:0,
                    cursor:'pointer',font:'inherit',color:'var(--text)',
                    fontSize:'0.78rem',fontWeight:600,display:'inline-flex',
                    alignItems:'center',gap:5}}>
                  {listOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                  Hiányzó bevallások ({tetelek.length})
                </button>
                {listOpen && hb.rendezett_db > 0 && (
                  <Checkbox label="a rendezettek is" checked={showResolved}
                    onChange={e => setShowResolved(e.target.checked)}/>)}
              </div>
              {listOpen && (<>
              <div style={{overflowX:'auto'}}>
                {/* Fix layout: a „rendezettek is" szűrő ne méretezze át az
                    oszlopokat (ugyanaz az elv, mint a NAV tétel-tábláknál). */}
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:760,
                  tableLayout:'fixed'}}>
                  <colgroup>
                    <col style={{width:116}}/>
                    <col style={{width:110}}/>
                    <col style={{width:132}}/>
                    <col/>
                    <col style={{width:104}}/>
                  </colgroup>
                  <thead><tr>
                    <th style={th}>Esedékesség</th>
                    <th style={th}>Nyomtatvány</th>
                    <th style={th}>Időszak</th>
                    <th style={th}>Megnevezés</th>
                    <th style={th}>Állapot</th>
                  </tr></thead>
                  <tbody>
                    {tetelek.map(t => (
                      <tr key={t.id} style={{opacity: t.resolved_at ? 0.6 : 1}}>
                        <td style={{...td,whiteSpace:'nowrap'}}>
                          {fmtDate(t.eredeti_esedekesseg)}</td>
                        <td style={{...td,fontFamily:'var(--mono)'}}>
                          {t.bevallas_tipus || '—'}</td>
                        <td style={{...td,whiteSpace:'nowrap'}}>
                          {idoszakLabel(t)}</td>
                        {/* A NAV bevallás-megnevezései ~90 karakteresek („Bevallás
                            a kifizetésekkel, juttatásokkal összefüggő adóról,
                            járulékokról és egyéb adatokról") — csonkolva, a teljes
                            szöveg tooltipben. */}
                        <td style={td}>
                          <Truncate>{t.bevallas_megnevezes || '—'}</Truncate></td>
                        <td style={td}>
                          {t.resolved_at ? (
                            /* ⚠ JÓ HÍR: a NAV már nem jelzi hiányzónak. */
                            <span style={{display:'inline-flex',alignItems:'center',
                              gap:4,fontSize:'0.72rem',color:'var(--success)'}}>
                              <CheckCircle2 size={12}/>beadva
                            </span>
                          ) : (
                            <span style={{display:'inline-flex',alignItems:'center',
                              gap:4,fontSize:'0.72rem',color:'var(--warning)'}}>
                              <FileWarning size={12}/>hiányzik
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{fontSize:'var(--fs-xs)',color:'var(--text-muted)',
                margin:'8px 0 0'}}>
                A „beadva" azt jelenti, hogy a NAV már nem jelzi hiányzónak —
                a sor megmarad, mert a „mikor hiányzott / mikor rendeződött"
                előzmény compliance-bizonyíték.
              </p>
              </>)}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
